import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { AuditTrail } from '@/components/audit/AuditTrail';
import { createFileRoute, Link, useRouter, Outlet, useChildMatches } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, MoveRight, Paperclip, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { StatusBadge } from '@/components/molecules/StatusBadge';
import {
  formatDate,
  formatDateTime,
} from '@/components/organisms/estimates/support/estimate-ui-helpers';
import { Skeleton } from '@/components/atoms/skeleton';
import { useProposal } from '@/hooks/proposals/useProposal';
import { useProposalAttachments } from '@/hooks/proposals/useProposalAttachments';
import type { Proposal, ProposalStatus } from '@/types/proposal';
import { ProposalActions } from '@/components/organisms/proposals/ProposalActions';
import {
  ProposalOptionBreakdown,
  type ProposalDetailOption,
} from '@/components/organisms/proposals/ProposalDetailOptionsSection';
import { membershipBillingSummaryFromLineItems } from '@/lib/membership/format-plan-summary';
import {
  OPTION_COLORS,
  getProposalOptionDepositAmount,
} from '@/components/organisms/proposals/support/proposal-option-math';
import { ProposalVersionHistory } from '@/components/organisms/proposals/ProposalVersionHistory';
import { DetailCard, StatusBanner } from '@/components/molecules';
import { DetailPageLayout, useWorkspaceHeader } from '@/components/layouts';
import { EstimateAttachments } from '@/components/organisms/estimates/EstimateAttachments';
import { SalesDocumentNotesDocsSection } from '@/components/organisms/sales/SalesDocumentDetailSections';
import { hasProposalNotesDocs } from './_shared';
import { SalesAddressBlock } from '@/components/organisms/sales/SalesAddressBlock';
import { SalesContactBlock } from '@/components/organisms/sales/SalesContactBlock';
import {
  SalesLifecycleStepper,
  type SalesLifecycleDetour,
  type SalesLifecycleStep,
} from '@/components/organisms/sales/SalesLifecycleStepper';
import { getProposalNextStepCopy } from '@/components/organisms/proposals/support/proposal-next-step';
import {
  STATUS_FILTERS,
  fmt as formatCurrency,
} from '@/components/organisms/proposals/support/proposal-helpers';
import { cn } from '@/lib/utils';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { formatDateOnly } from '@/lib/dates/date-only';
import { summarizeProposalOptions } from '@/components/organisms/proposals/support/proposal-option-summary';

const PROPOSAL_TABS = ['overview', 'options', 'notes', 'internal'] as const;
type ProposalTab = (typeof PROPOSAL_TABS)[number];

export const Route = createFileRoute('/_dashboard/dashboard/proposals/$id')({
  validateSearch: (search): { tab: ProposalTab } => ({
    tab: PROPOSAL_TABS.includes(search.tab as ProposalTab)
      ? (search.tab as ProposalTab)
      : 'overview',
  }),
  component: ProposalDetailPage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface ProposalAddress {
  street?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

function formatProposalStatusLabel(status: ProposalStatus): string {
  return STATUS_FILTERS.find((option) => option.value === status)?.label ?? status;
}

function formatTitleCase(value: string): string {
  return value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatProposalAddress(address?: ProposalAddress | null): string {
  if (!address) return '';
  return [
    address.street,
    address.street2,
    [address.city, address.state, address.zip].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ');
}

function formatRelativeProposalStatusTime({
  status,
  sentAt,
  viewedAt,
  approvedAt,
  rejectedAt,
  expiredAt,
  updatedAt,
  createdAt,
}: {
  status: ProposalStatus;
  sentAt?: string | null;
  viewedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  expiredAt?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
}): string {
  const timestamp =
    (status === 'SENT' && sentAt) ||
    (status === 'VIEWED' && viewedAt) ||
    (status === 'RESOLVED' && approvedAt) ||
    (status === 'DECLINED' && rejectedAt) ||
    (status === 'EXPIRED' && expiredAt) ||
    updatedAt ||
    createdAt;

  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true });
}

function optionTotalRange(minimum: number | null, maximum: number | null): string {
  if (minimum === null || maximum === null) return 'Not priced';
  return minimum === maximum
    ? formatCurrency(minimum)
    : `${formatCurrency(minimum)} – ${formatCurrency(maximum)}`;
}

function ProposalContextHeaderSlot({
  proposalNumber,
  status,
  createdAt,
  updatedAt,
  sentAt,
  viewedAt,
  approvedAt,
  rejectedAt,
  expiredAt,
}: {
  proposalNumber: string;
  status: ProposalStatus;
  createdAt: string;
  updatedAt?: string | null;
  sentAt?: string | null;
  viewedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  expiredAt?: string | null;
}) {
  const relativeStatusTime = useMemo(
    () =>
      formatRelativeProposalStatusTime({
        status,
        sentAt,
        viewedAt,
        approvedAt,
        rejectedAt,
        expiredAt,
        updatedAt,
        createdAt,
      }),
    [status, sentAt, viewedAt, approvedAt, rejectedAt, expiredAt, updatedAt, createdAt],
  );

  const contextHeader = useMemo(() => {
    const subtitle = [formatProposalStatusLabel(status), relativeStatusTime]
      .filter(Boolean)
      .join(' · ');

    return (
      <div className="flex min-w-0 flex-col">
        <h2 className="truncate text-base font-semibold">{proposalNumber}</h2>
        <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
      </div>
    );
  }, [proposalNumber, status, relativeStatusTime]);

  useWorkspaceHeader({ contextHeader }, [contextHeader]);
  return null;
}

function ProposalSummaryCard({
  id,
  proposalNumber,
  title,
  subtitle,
  status,
  revisionNumber,
  customer,
  contactId,
  serviceAddress,
  billingAddress,
  optionCount,
  optionPriceRange,
  actions,
  onBack,
}: {
  id: string;
  proposalNumber: string;
  title: string;
  subtitle: string;
  status: ProposalStatus;
  revisionNumber: number;
  customer: Proposal['customer'];
  contactId: string | null;
  serviceAddress: string;
  billingAddress: string;
  optionCount: number;
  optionPriceRange: string;
  actions: ReactNode;
  onBack: () => void;
}) {
  const pricingHidden = useIsFieldHidden('PRICING');

  return (
    <section className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Button
            variant="ghost"
            size="icon-sm"
            className="-ml-1 shrink-0 md:hidden"
            onClick={onBack}
            aria-label="Back to proposals"
          >
            <ArrowLeft size={16} />
          </Button>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {proposalNumber}
            </p>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-semibold tracking-normal">{title}</h1>
              <StatusBadge status={status} className="rounded-md" />
              {revisionNumber > 0 ? <Badge variant="outline">Rev #{revisionNumber}</Badge> : null}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{subtitle || `Proposal ${id}`}</p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-2 lg:items-end">
          <div className="flex max-w-full overflow-hidden rounded-lg border bg-muted/25 text-left">
            <div className="min-w-[5.5rem] px-3 py-2">
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Options
              </p>
              <p className="text-lg font-semibold tabular-nums">{optionCount}</p>
            </div>
            {!pricingHidden ? (
              <div className="min-w-0 border-l px-3 py-2">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Price range
                </p>
                <p className="break-words text-lg font-semibold tabular-nums">{optionPriceRange}</p>
              </div>
            ) : null}
          </div>
          {actions}
        </div>
      </div>

      <div className="mt-5 space-y-4 border-t pt-4">
        <SalesContactBlock
          label="Customer"
          name={customer.name}
          email={customer.email}
          phone={customer.phone}
          contactId={contactId}
          mapAddress={serviceAddress}
        />
        <div className="border-t pt-4">
          <SalesAddressBlock service={serviceAddress} billing={billingAddress} />
        </div>
      </div>
    </section>
  );
}

function buildProposalLifecycleSteps({
  status,
  createdAt,
  sentAt,
  viewedAt,
  approvedAt,
}: {
  status: ProposalStatus;
  createdAt?: string | null;
  sentAt?: string | null;
  viewedAt?: string | null;
  approvedAt?: string | null;
}): SalesLifecycleStep[] {
  const resolvedAt = status === 'RESOLVED' ? approvedAt : null;

  return [
    {
      key: 'created',
      label: 'Draft',
      dateLabel: createdAt ? formatDateTime(createdAt) : null,
      state: ['DRAFT', 'REVIEW', 'REVISED'].includes(status) ? 'current' : 'done',
    },
    {
      key: 'sent',
      label: 'Sent',
      dateLabel: sentAt ? formatDateTime(sentAt) : null,
      state: sentAt ? (['SENT', 'FOLLOW_UP'].includes(status) ? 'current' : 'done') : 'pending',
    },
    {
      key: 'viewed',
      label: 'Viewed',
      dateLabel: viewedAt ? formatDateTime(viewedAt) : null,
      state: viewedAt ? (status === 'VIEWED' ? 'current' : 'done') : 'pending',
    },
    {
      key: 'decided',
      label: 'Decided',
      dateLabel: resolvedAt ? formatDateTime(resolvedAt) : null,
      state: resolvedAt ? 'current' : 'pending',
    },
  ];
}

function buildProposalLifecycleDetours({
  status,
  rejectedAt,
  expiredAt,
}: {
  status: ProposalStatus;
  rejectedAt?: string | null;
  expiredAt?: string | null;
}): SalesLifecycleDetour[] {
  if (status === 'DECLINED') {
    return [
      {
        key: 'declined',
        label: 'Declined',
        dateLabel: rejectedAt ? formatDateTime(rejectedAt) : null,
        variant: 'destructive',
      },
    ];
  }

  if (status === 'EXPIRED') {
    return [
      {
        key: 'expired',
        label: 'Expired',
        dateLabel: expiredAt ? formatDateTime(expiredAt) : null,
        variant: 'warning',
      },
    ];
  }

  return [];
}

function ProposalOutcomeCard({
  status,
  nextStepCopy,
  materializedEstimateId,
  selectedOption,
  createdAt,
  sentAt,
  viewedAt,
  approvedAt,
  rejectedAt,
  expiredAt,
  signatureName,
  signatureUrl,
  signatureAt,
}: {
  status: ProposalStatus;
  nextStepCopy: string;
  materializedEstimateId?: string | null;
  selectedOption?: ProposalDetailOption | null;
  createdAt?: string | null;
  sentAt?: string | null;
  viewedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  expiredAt?: string | null;
  signatureName?: string | null;
  /** Presigned by the server on every read; renders as-is. */
  signatureUrl?: string | null;
  signatureAt?: string | null;
}) {
  const isResolved = status === 'RESOLVED';
  const pricingHidden = useIsFieldHidden('PRICING');
  const depositAmount = selectedOption ? getProposalOptionDepositAmount(selectedOption) : 0;

  return (
    <section className="space-y-4 rounded-xl border bg-muted/45 px-4 py-3.5">
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
          <MoveRight size={12} />
          {isResolved ? 'Outcome' : 'Next step'}
        </p>
        {isResolved && selectedOption ? (
          <p className="mt-1 text-[15px] font-medium text-foreground">
            Resolved · customer chose{' '}
            <span className="font-semibold">{selectedOption.label || 'selected option'}</span>
            {!pricingHidden ? (
              <>
                {' '}
                — {formatCurrency(selectedOption.total)}
                {depositAmount > 0 ? ` · deposit ${formatCurrency(depositAmount)}` : ''}
              </>
            ) : null}
            {materializedEstimateId ? (
              <>
                {' '}
                <Link
                  to="/dashboard/estimates/$id"
                  params={{ id: materializedEstimateId }}
                  search={{ tab: 'overview' } as never}
                  className="text-primary hover:underline"
                >
                  Open estimate →
                </Link>
              </>
            ) : null}
          </p>
        ) : (
          <p className="mt-1 text-[15px] font-medium text-foreground">
            {nextStepCopy}
            {materializedEstimateId ? (
              <>
                {' '}
                <Link
                  to="/dashboard/estimates/$id"
                  params={{ id: materializedEstimateId }}
                  search={{ tab: 'overview' } as never}
                  className="text-primary hover:underline"
                >
                  Open estimate →
                </Link>
              </>
            ) : null}
          </p>
        )}
        {isResolved && signatureName ? (
          <div className="mt-2 flex items-center gap-3">
            {signatureUrl ? (
              <img
                src={signatureUrl}
                alt={`Signature of ${signatureName}`}
                className="h-9 w-auto max-w-[140px] rounded border bg-white object-contain px-1.5"
              />
            ) : null}
            <p className="text-xs text-muted-foreground">
              Signed by <span className="font-medium text-foreground">{signatureName}</span>
              {signatureAt ? ` · ${formatDateTime(signatureAt)}` : ''}
            </p>
          </div>
        ) : null}
      </div>
      <SalesLifecycleStepper
        steps={buildProposalLifecycleSteps({
          status,
          createdAt,
          sentAt,
          viewedAt,
          approvedAt,
        })}
        detours={buildProposalLifecycleDetours({
          status,
          rejectedAt,
          expiredAt,
        })}
      />
    </section>
  );
}

function optionDisplayLabel(option: ProposalDetailOption | undefined, index: number): string {
  return option?.label || `Option ${index + 1}`;
}

function CompactOptionRow({
  option,
  index,
  selected,
}: {
  option: ProposalDetailOption;
  index: number;
  selected?: boolean;
}) {
  const pricingHidden = useIsFieldHidden('PRICING');
  const optionColor = option.color ?? OPTION_COLORS[index % OPTION_COLORS.length];
  const membershipBillingSummary = membershipBillingSummaryFromLineItems(option.lineItems);
  // A proposal with nothing chosen yet renders only these compact rows — the
  // full breakdown is behind "View all options" — so without this the deposit
  // was invisible on the detail page for the whole of a proposal's life before
  // acceptance, which is precisely when the operator is checking it.
  const depositAmount = getProposalOptionDepositAmount(option);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-md border border-l-2 px-3 py-2',
        selected ? 'border-success/30 bg-success/10' : 'bg-card',
      )}
      style={{ borderLeftColor: optionColor }}
    >
      <div className="min-w-0">
        <p className="flex min-w-0 items-center gap-2 text-sm font-medium">
          <span
            className="size-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: optionColor }}
          />
          <span className="truncate">{optionDisplayLabel(option, index)}</span>
          {selected ? <span className="ml-2 text-xs text-success">Chosen</span> : null}
        </p>
        <p className="text-xs text-muted-foreground">
          {option.lineItems.length} item{option.lineItems.length === 1 ? '' : 's'}
          {membershipBillingSummary ? ` · ${membershipBillingSummary}` : ''}
          {!pricingHidden && depositAmount > 0 ? ` · deposit ${formatCurrency(depositAmount)}` : ''}
        </p>
      </div>
      {!pricingHidden ? (
        <p className="shrink-0 text-sm font-semibold tabular-nums">
          {formatCurrency(option.total)}
        </p>
      ) : null}
    </div>
  );
}

function ProposalOptionsInlineSection({
  options,
  selectedOptionIndex,
  onOpenAll,
}: {
  options: ProposalDetailOption[];
  selectedOptionIndex: number | null;
  onOpenAll: () => void;
}) {
  const pricingHidden = useIsFieldHidden('PRICING');

  if (options.length === 0) {
    return (
      <DetailCard label="Options">
        <p className="text-sm text-muted-foreground">No options yet.</p>
      </DetailCard>
    );
  }

  const selectedOption = selectedOptionIndex != null ? options[selectedOptionIndex] : undefined;
  const selectedOptionColor =
    selectedOption && selectedOptionIndex != null
      ? (selectedOption.color ?? OPTION_COLORS[selectedOptionIndex % OPTION_COLORS.length])
      : undefined;
  const otherOptions = options
    .map((option, index) => ({ option, index }))
    .filter(({ index }) => index !== selectedOptionIndex);

  return (
    <DetailCard label="Options" bodyClassName="space-y-4">
      {selectedOption ? (
        <>
          <div className="rounded-lg border border-success/30 bg-success/5 p-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-success">
                  Chosen option
                </p>
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: selectedOptionColor }}
                  />
                  {optionDisplayLabel(selectedOption, selectedOptionIndex ?? 0)}
                </h3>
              </div>
              <Badge className="border-success/30 bg-success/10 text-success">Chosen</Badge>
            </div>
            <ProposalOptionBreakdown option={selectedOption} chosen />
          </div>

          <div className="rounded-lg border bg-card px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Other options offered:{' '}
                {otherOptions.length > 0
                  ? otherOptions
                      .map(({ option, index }) => {
                        const itemCount = option.lineItems.length;
                        return `${optionDisplayLabel(option, index)} · ${itemCount} item${
                          itemCount === 1 ? '' : 's'
                        }${pricingHidden ? '' : ` · ${formatCurrency(option.total)}`}`;
                      })
                      .join(' | ')
                  : 'none'}
              </p>
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={onOpenAll}>
                View all options
                <ArrowRight className="ml-1 size-3.5" />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-2">
            {options.map((option, index) => (
              <CompactOptionRow key={`${option.label}-${index}`} option={option} index={index} />
            ))}
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={onOpenAll}>
              View all options
              <ArrowRight className="ml-1 size-3.5" />
            </Button>
          </div>
        </>
      )}
    </DetailCard>
  );
}

/** Exported for its test; the page is the only runtime caller. */
export function ProposalOptionsModal({
  open,
  onOpenChange,
  options,
  selectedOptionIndex,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: ProposalDetailOption[];
  selectedOptionIndex: number | null;
}) {
  const pricingHidden = useIsFieldHidden('PRICING');
  const defaultIndex =
    selectedOptionIndex != null && options[selectedOptionIndex] ? selectedOptionIndex : 0;
  const [activeIndex, setActiveIndex] = useState(defaultIndex);

  useEffect(() => {
    if (open) setActiveIndex(defaultIndex);
  }, [defaultIndex, open]);

  const activeOption = options[activeIndex] ?? options[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `sm:` because the atom sets `sm:max-w-lg`, and a bare `max-w-5xl`
          loses to it at every desktop width — the dialog rendered at 512px
          and the line-item table inside wrapped names one letter per line. */}
      <DialogContent className="max-h-[86vh] overflow-hidden p-0 sm:max-w-5xl">
        <DialogHeader className="border-b px-5 py-4">
          <DialogTitle>Proposal options</DialogTitle>
        </DialogHeader>
        <div className="grid min-h-0 gap-4 overflow-y-auto px-5 py-4">
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {options.map((option, index) => {
              const active = index === activeIndex;
              const chosen = index === selectedOptionIndex;
              const optionColor = option.color ?? OPTION_COLORS[index % OPTION_COLORS.length];
              return (
                <button
                  key={`${option.label}-${index}`}
                  type="button"
                  className={cn(
                    'rounded-lg border bg-card p-3 text-left transition-colors hover:bg-muted/35',
                    active ? 'border-foreground shadow-sm' : 'border-border/70',
                  )}
                  onClick={() => setActiveIndex(index)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="size-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: optionColor }}
                        />
                        <p className="truncate text-sm font-semibold">
                          {optionDisplayLabel(option, index)}
                        </p>
                      </div>
                      {!pricingHidden ? (
                        <p className="mt-1 text-lg font-semibold tabular-nums">
                          {formatCurrency(option.total)}
                        </p>
                      ) : null}
                      <p className="text-xs text-muted-foreground">
                        {option.lineItems.length} item
                        {option.lineItems.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    {chosen ? (
                      <Badge className="border-success/30 bg-success/10 text-success">Chosen</Badge>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>

          {activeOption ? (
            <div className="max-h-[48vh] overflow-y-auto rounded-lg border p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Active option
                  </p>
                  <h3 className="text-base font-semibold">
                    {optionDisplayLabel(activeOption, activeIndex)}
                  </h3>
                </div>
                {!pricingHidden ? (
                  <p className="text-base font-semibold tabular-nums">
                    {formatCurrency(activeOption.total)}
                  </p>
                ) : null}
              </div>
              <ProposalOptionBreakdown
                option={activeOption}
                chosen={activeIndex === selectedOptionIndex}
              />
            </div>
          ) : null}
        </div>
        <DialogFooter className="border-t px-5 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ProposalDetailPage() {
  const router = useRouter();
  const { id } = Route.useParams();
  const childMatches = useChildMatches();

  const { data: rawProposal, isLoading, isError, error } = useProposal(id);
  const { data: attachmentData = [] } = useProposalAttachments(id);

  const [showSkeleton, setShowSkeleton] = useState(false);
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  useEffect(() => {
    if (!isLoading) return;
    const t = setTimeout(() => setShowSkeleton(true), 150);
    return () => clearTimeout(t);
  }, [isLoading]);

  // When navigating to a child route (e.g. /edit), render it instead of detail
  if (childMatches.length > 0) {
    return <Outlet />;
  }

  // -- Loading ---------------------------------------------------------------

  if (isLoading) {
    if (!showSkeleton) return null;
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // -- Error -----------------------------------------------------------------

  if (isError || !rawProposal) {
    return (
      <div className="space-y-4 p-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Proposal not found'}
        </div>
        <Button
          variant="ghost"
          size="sm"
          // @ts-expect-error — search required by TanStack Router v1.168
          onClick={() => router.navigate({ to: '/dashboard/proposals' })}
        >
          <ArrowLeft className="mr-1 size-4" />
          Back to Proposals
        </Button>
      </div>
    );
  }

  // -- Normalize -------------------------------------------------------------

  const raw = rawProposal as Proposal & Record<string, unknown>;
  const proposal = {
    status: raw.status as ProposalStatus,
    proposalNumber: raw.proposalNumber ?? '',
    customer: raw.customer ?? { name: '', email: '', phone: '', address: '' },
    contactId: (raw.contactId as string | null | undefined) ?? null,
    options: ((raw.options ?? []) as unknown as Record<string, unknown>[]).map((opt) => ({
      label: String(opt.label ?? ''),
      description: String(opt.description ?? ''),
      color: opt.color != null ? String(opt.color) : undefined,
      pricingMode:
        opt.pricingMode === 'TM_OVERRIDE' ? ('TM_OVERRIDE' as const) : ('STANDARD' as const),
      discountType:
        opt.discountType === 'PERCENT' || opt.discountType === 'FLAT'
          ? (opt.discountType as 'PERCENT' | 'FLAT')
          : null,
      discountValue: opt.discountValue != null ? Number(opt.discountValue) : null,
      depositType:
        opt.depositType === 'PERCENT' || opt.depositType === 'FLAT'
          ? (opt.depositType as 'PERCENT' | 'FLAT')
          : null,
      depositValue: opt.depositValue != null ? Number(opt.depositValue) : null,
      depositRequired: Boolean(opt.depositRequired),
      lineItems: ((opt.lineItems ?? []) as Record<string, unknown>[]).map((li) => ({
        ...li,
        lineItemId: String(li.lineItemId ?? ''),
        name: String(li.name ?? ''),
        code: String(li.code ?? ''),
        type: String(li.type ?? ''),
        basePrice: Number(li.basePrice ?? 0),
        quantity: Number(li.quantity ?? 1),
        lineTotal: Number(li.lineTotal ?? Number(li.basePrice ?? 0) * Number(li.quantity ?? 1)),
      })),
      subtotal: Number(opt.subtotal ?? 0),
      taxTotal: Number(opt.taxTotal ?? 0),
      total: Number(opt.total ?? 0),
    })),
    selectedOptionIndex: raw.selectedOptionIndex ?? null,
    materializedEstimateId: raw.materializedEstimateId ?? null,
    serviceAddress: (raw.serviceAddress as Proposal['serviceAddress']) ?? null,
    billingAddress: (raw.billingAddress as Proposal['billingAddress']) ?? null,
    grandTotal: Number(raw.grandTotal ?? 0),
    createdAt: raw.createdAt ?? '',
    updatedAt: raw.updatedAt ?? '',
    validUntil: raw.validUntil,
    sentAt: raw.sentAt,
    viewedAt: (raw.viewedAt as string | null | undefined) ?? null,
    approvedAt: raw.approvedAt,
    rejectedAt: raw.rejectedAt,
    expiredAt: raw.expiredAt,
    declineReason: (raw.declineReason as string | null | undefined) ?? null,
    notes: raw.notes ?? '',
    terms: String(raw.terms ?? raw.termsAndConditions ?? ''),
    internalNotes: raw.internalNotes ?? '',
    followUpDate: raw.followUpDate ?? null,
    revisionNumber: Number(raw.revisionNumber) || 0,
    draftVersion: Number(raw.draftVersion) || 0,
    previousVersions: raw.previousVersions ?? [],
  };

  // -- Derived ---------------------------------------------------------------

  const status = proposal.status;
  const publicAttachments = attachmentData.filter((a) => !a.isInternal);
  const internalAttachments = attachmentData.filter((a) => a.isInternal);
  const isDeclined = status === 'DECLINED';
  const isResolved = status === 'RESOLVED';
  const hasInternalNotes = String(proposal.internalNotes ?? '').trim().length > 0;
  const hasNotesDocs = hasProposalNotesDocs({
    notes: proposal.notes,
    terms: proposal.terms,
    publicAttachmentCount: publicAttachments.length,
  });
  const customerName = proposal.customer.name || 'Unknown customer';
  const serviceAddress = formatProposalAddress(proposal.serviceAddress);
  const billingAddress = formatProposalAddress(proposal.billingAddress);
  const proposalTitle = `${customerName} - Proposal`;
  const approvalMethod = raw.customerApprovalMethod ? String(raw.customerApprovalMethod) : null;
  const proposalSubtitle = [
    proposal.proposalNumber,
    `Created ${formatDate(proposal.createdAt)}`,
    proposal.validUntil ? `Valid until ${formatDateOnly(String(proposal.validUntil))}` : '',
    approvalMethod ? `Approval: ${formatTitleCase(approvalMethod)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const optionSummary = summarizeProposalOptions(proposal.options);
  const optionPriceRange = optionTotalRange(optionSummary.minimum, optionSummary.maximum);
  const nextStepCopy = getProposalNextStepCopy({
    status,
    followUpDate: proposal.followUpDate,
    materializedEstimateId: proposal.materializedEstimateId,
    formatDate,
  });
  const selectedOptionIndex =
    proposal.selectedOptionIndex != null ? Number(proposal.selectedOptionIndex) : null;
  const selectedOption =
    selectedOptionIndex != null
      ? (proposal.options[selectedOptionIndex] as ProposalDetailOption | undefined)
      : undefined;

  // -- Render ----------------------------------------------------------------

  return (
    <DetailPageLayout key={id}>
      <ProposalContextHeaderSlot
        proposalNumber={proposal.proposalNumber}
        status={status}
        createdAt={String(proposal.createdAt)}
        updatedAt={String(proposal.updatedAt)}
        sentAt={proposal.sentAt as string | null | undefined}
        viewedAt={proposal.viewedAt}
        approvedAt={proposal.approvedAt as string | null | undefined}
        rejectedAt={proposal.rejectedAt as string | null | undefined}
        expiredAt={proposal.expiredAt as string | null | undefined}
      />

      <ProposalSummaryCard
        id={id}
        proposalNumber={proposal.proposalNumber}
        title={proposalTitle}
        subtitle={proposalSubtitle}
        status={status}
        revisionNumber={proposal.revisionNumber}
        customer={proposal.customer}
        contactId={proposal.contactId}
        serviceAddress={serviceAddress}
        billingAddress={billingAddress}
        optionCount={optionSummary.count}
        optionPriceRange={optionPriceRange}
        actions={
          <ProposalActions
            id={id}
            proposalNumber={proposal.proposalNumber}
            status={status}
            sentAt={proposal.sentAt as string | null | undefined}
            selectedOptionIndex={selectedOptionIndex}
            materializedEstimateId={proposal.materializedEstimateId}
            variant="header-row"
          />
        }
        onBack={() =>
          void router.navigate({
            to: '/dashboard/proposals',
            search: { status: 'all', q: '', page: 1 } as never,
          })
        }
      />

      <ProposalOutcomeCard
        signatureName={raw.customerSignatureName ?? null}
        signatureUrl={raw.customerSignatureUrl ?? null}
        signatureAt={raw.customerSignatureAt ?? null}
        status={status}
        nextStepCopy={nextStepCopy}
        materializedEstimateId={proposal.materializedEstimateId}
        selectedOption={selectedOption ?? null}
        createdAt={String(proposal.createdAt)}
        sentAt={proposal.sentAt as string | null | undefined}
        viewedAt={proposal.viewedAt}
        approvedAt={proposal.approvedAt as string | null | undefined}
        rejectedAt={proposal.rejectedAt as string | null | undefined}
        expiredAt={proposal.expiredAt as string | null | undefined}
      />

      {isResolved ? (
        <div className="flex items-center gap-2 rounded-lg border border-success/20 bg-success/10 px-4 py-2.5 text-sm text-success">
          <CheckCircle2 size={15} className="shrink-0" />
          <span className="font-medium">Resolved</span>
          {proposal.approvedAt ? (
            <span className="ml-auto text-xs text-success/80">
              {formatDate(proposal.approvedAt)}
            </span>
          ) : null}
        </div>
      ) : null}

      {isDeclined ? (
        <StatusBanner
          icon={X}
          label="Declined"
          sublabel={proposal.declineReason}
          date={proposal.rejectedAt ? formatDateTime(proposal.rejectedAt) : null}
          variant="red"
        />
      ) : null}

      <ProposalOptionsInlineSection
        options={proposal.options as ProposalDetailOption[]}
        selectedOptionIndex={selectedOptionIndex}
        onOpenAll={() => setOptionsModalOpen(true)}
      />

      {hasNotesDocs ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Paperclip className="size-4 text-muted-foreground" />
            Notes, terms & documents
          </div>
          <SalesDocumentNotesDocsSection
            notes={proposal.notes}
            terms={proposal.terms}
            // Proposal terms are authored in `TermsEditor` — a rich-text editor
            // whose "Templates" dropdown pastes a T&C repository entry verbatim
            // — so this document's terms are editor HTML, and the default
            // plain-text treatment showed the raw markup instead of the terms.
            allowHtmlTerms
            attachmentsCount={publicAttachments.length}
            renderAttachments={
              <EstimateAttachments proposalId={id} attachments={publicAttachments} readOnly />
            }
            showEmptyWhenNoContent={false}
          />
        </div>
      ) : null}

      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Lock className="size-4 text-muted-foreground" />
          Internal
        </div>
        {hasInternalNotes ? (
          <DetailCard label="Internal Notes">
            <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
              {proposal.internalNotes}
            </p>
          </DetailCard>
        ) : null}

        {internalAttachments.length > 0 ? (
          <DetailCard label={`Internal Attachments (${internalAttachments.length})`}>
            <EstimateAttachments proposalId={id} attachments={internalAttachments} readOnly />
          </DetailCard>
        ) : null}

        {proposal.previousVersions.length > 0 ? (
          <DetailCard label={`Version History (${proposal.previousVersions.length})`}>
            <ProposalVersionHistory versions={proposal.previousVersions} />
          </DetailCard>
        ) : null}

        <AuditTrail entityType="proposal" entityId={id} alwaysOpen />
      </div>

      <ProposalOptionsModal
        open={optionsModalOpen}
        onOpenChange={setOptionsModalOpen}
        options={proposal.options as ProposalDetailOption[]}
        selectedOptionIndex={selectedOptionIndex}
      />
    </DetailPageLayout>
  );
}
